import type { ObservableValue } from '@amazon/vinyl-observable'
import type { Disposable, ErrorLevel, Unsubscribe } from '@amazon/vinyl-util'
import type { PlayerKind } from '../core/SubPlayer'

export enum ConnectionStatus {
    DISCONNECTED = 'disconnected',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    ERRED = 'erred',
}

/**
 * Moves strings between the two ends of the bridge. A transport may carry unrelated traffic as
 * well; the bridge ignores any message whose `ch` isn't {@link PLAYBACK_BRIDGE_CHANNEL}.
 */
export interface BridgeTransport extends Disposable {
    readonly status$: ObservableValue<ConnectionStatus>
    send(message: string): void
    onMessage(listener: (message: string) => void): Unsubscribe
}

export type JsonValue =
    | null
    | boolean
    | number
    | string
    | readonly JsonValue[]
    | { readonly [key: string]: JsonValue }

export type JsonObject = { readonly [key: string]: JsonValue }

export const PLAYBACK_BRIDGE_CHANNEL = 'amuPlayback'

export interface ProtocolVersion {
    readonly major: number
    /** A minor version only adds optional fields and methods. */
    readonly minor: number
}

export const PLAYBACK_PROTOCOL: ProtocolVersion = { major: 1, minor: 4 }

/** The current major and, once there is one, the major before it. */
export const PLAYBACK_PROTOCOLS: readonly ProtocolVersion[] = [PLAYBACK_PROTOCOL]

export type BridgeTarget =
    | 'player'
    | 'playlist'
    | 'cloudQueue'
    | 'streamingSettings'
    | 'sleepTimer'
    | 'prompt'
    | `remote:${string}`

export interface WireError {
    readonly name: string
    readonly message: string
    readonly level: ErrorLevel
    readonly origin: string
}

export interface HostCapabilities {
    /** Engines the host plays itself; the rest are placed on the client. */
    readonly kinds: readonly PlayerKind[]
    readonly backgroundPlayback: boolean
    readonly mediaControls: boolean
    /** Since 1.1: the host remembers a picked speed through `player.rememberPlaybackRate`. */
    readonly playbackRatePreference?: boolean
    /** Since 1.2: the host drops its restore snapshot on `cloudQueue.clearRestoreSnapshot`. */
    readonly queueRestoreClear?: boolean
}

/**
 * The payloads the playback layers define; the RPC layer only checks that each is an object.
 * `PlaybackBridgePayloads` is the playback bridge's own.
 */
export interface BridgePayloads {
    readonly profile: object
    readonly session: object
    readonly sessionChanges: object
    readonly snapshot: object
    readonly patch: object
    readonly notice: object
}

/** Payloads left untyped, for a channel that doesn't look inside them. */
export interface JsonPayloads extends BridgePayloads {
    readonly profile: JsonObject
    readonly session: JsonObject
    readonly sessionChanges: JsonObject
    readonly snapshot: JsonObject
    readonly patch: JsonObject
    readonly notice: JsonObject
}

type Envelope<K extends string, B> = {
    readonly ch: typeof PLAYBACK_BRIDGE_CHANNEL
    readonly kind: K
} & B

/** Client -> host, first after the transport connects, and again after any reload. */
export type HelloMessage<P extends BridgePayloads = JsonPayloads> = Envelope<
    'hello',
    {
        readonly protocols: readonly ProtocolVersion[]
        readonly clientId: string
        readonly profile: P['profile']
        readonly session: P['session']
    }
>

/** Host -> client, answering a `hello` or a `resync`, or unasked when the host returns to the foreground. */
export type WelcomeMessage<P extends BridgePayloads = JsonPayloads> = Envelope<
    'welcome',
    {
        readonly protocol: ProtocolVersion
        readonly hostId: string
        readonly capabilities: HostCapabilities
        readonly snapshot: P['snapshot']
    }
>

/** Host -> client, answering a `hello` that shares no major with the host. */
export type IncompatibleMessage = Envelope<
    'incompatible',
    { readonly protocols: readonly ProtocolVersion[] }
>

export type RequestMessage = Envelope<
    'request',
    {
        readonly id: string
        readonly target: BridgeTarget
        readonly method: string
        readonly args: readonly JsonValue[]
    }
>

export type ResponseBody =
    | { readonly ok: true; readonly value: JsonValue }
    | { readonly ok: false; readonly error: WireError }

/** Exactly one per `request`, with the same `id`. A method that returns nothing answers `null`. */
export type ResponseMessage = Envelope<'response', { readonly id: string } & ResponseBody>

/** `patch` and `event` share one `seq` counter. */
export type PatchMessage<P extends BridgePayloads = JsonPayloads> = Envelope<
    'patch',
    { readonly seq: number; readonly changes: P['patch'] }
>

export type EventMessage = Envelope<
    'event',
    {
        readonly seq: number
        readonly target: BridgeTarget
        readonly name: string
        readonly data: JsonValue
    }
>

export type NoticeMessage<P extends BridgePayloads = JsonPayloads> = Envelope<
    'notice',
    { readonly notice: P['notice'] }
>

export type ResyncMessage = Envelope<'resync', { readonly lastSeq: number | null }>

export type SessionMessage<P extends BridgePayloads = JsonPayloads> = Envelope<
    'session',
    { readonly changes: P['sessionChanges'] }
>

export type VisibilityMessage = Envelope<'visibility', { readonly hidden: boolean }>

/** Every message the bridge sends, each serialized as one JSON object. */
export type BridgeMessage<P extends BridgePayloads = JsonPayloads> =
    | HelloMessage<P>
    | WelcomeMessage<P>
    | IncompatibleMessage
    | RequestMessage
    | ResponseMessage
    | PatchMessage<P>
    | EventMessage
    | NoticeMessage<P>
    | ResyncMessage
    | SessionMessage<P>
    | VisibilityMessage

/** The messages the RPC layer passes through to the playback layers. */
export type BridgeNotification<P extends BridgePayloads = JsonPayloads> =
    | WelcomeMessage<P>
    | PatchMessage<P>
    | EventMessage
    | NoticeMessage<P>
    | ResyncMessage
    | SessionMessage<P>
    | VisibilityMessage

/** The highest major both ends speak, at the lower of their minors; null when none matches. */
export function negotiateProtocol(
    offered: readonly ProtocolVersion[],
    supported: readonly ProtocolVersion[]
): ProtocolVersion | null {
    let best: ProtocolVersion | null = null
    for (const theirs of offered) {
        const ours = supported.find(({ major }) => major === theirs.major)
        if (ours && (!best || ours.major > best.major)) {
            best = { major: ours.major, minor: Math.min(ours.minor, theirs.minor) }
        }
    }
    return best
}
